/**
 * Cookie-aware fetch helpers for the /v1/auth/* surface.
 *
 * The S13 auth flow uses HttpOnly session cookies plus a
 * non-HttpOnly CSRF cookie that the SPA reads via document.cookie
 * and re-emits in the X-CVP-CSRF header on every state-mutating
 * request. The SPA always sends ``credentials: 'include'`` so the
 * browser attaches both cookies on same-origin requests.
 *
 * All requests target the same backend the existing client.ts
 * uses; the base URL is read from localStorage so the dev
 * configuration carries through to the auth surface.
 */

import { loadConfig } from "../api/client";

export const COOKIE_NAME_CSRF = "cvp_csrf";
export const HEADER_NAME_CSRF = "X-CVP-CSRF";

export interface AuthApiError {
  status: number;
  code: string;
  message: string;
}

export interface MeResponse {
  user_id: string | null;
  service_account_id: string | null;
  email: string | null;
  display_name: string | null;
  tenant_id: string;
  role: string;
  mfa_enrolled: boolean;
}

export interface LoginOk {
  status: "ok";
  user_id: string;
  tenant_id: string;
  role: string;
}

export interface LoginNeedsMfa {
  status: "mfa_required";
  user_id: string;
}

export interface MfaEnrolStart {
  secret: string;
  provisioning_uri: string;
}

export interface MfaEnrolConfirm {
  status: "ok";
  recovery_codes: string[];
}

function readCookie(name: string): string | null {
  // document.cookie is a single string of "k=v; k=v"; we parse on
  // the fly so we always pick up the latest value (the browser
  // updates it after every Set-Cookie).
  const all = document.cookie.split(";");
  for (const raw of all) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

async function authFetch<T>(
  path: string,
  init?: RequestInit & { csrf?: boolean },
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  // The CSRF guard on the server side checks the X-CVP-CSRF header
  // for any state-mutating method on a cookie-authenticated route.
  // We attach it for all methods other than GET/HEAD/OPTIONS so the
  // caller does not have to remember which routes need it.
  const method = (init?.method ?? "GET").toUpperCase();
  const needsCsrf = init?.csrf ?? !["GET", "HEAD", "OPTIONS"].includes(method);
  if (needsCsrf) {
    const csrf = readCookie(COOKIE_NAME_CSRF);
    if (csrf !== null) {
      headers.set(HEADER_NAME_CSRF, csrf);
    }
  }
  const cfg = loadConfig();
  const response = await fetch(`${cfg.baseUrl}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  const body = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const err: AuthApiError = {
      status: response.status,
      code: body?.code ?? "error",
      message: body?.message ?? response.statusText,
    };
    throw err;
  }
  return body as T;
}

export const authApi = {
  me: (): Promise<MeResponse> => authFetch<MeResponse>("/v1/auth/me"),

  register: (
    email: string,
    password: string,
    displayName?: string,
  ): Promise<LoginOk> =>
    authFetch<LoginOk>("/v1/auth/register", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        display_name: displayName ?? null,
      }),
    }),

  login: (email: string, password: string): Promise<LoginOk | LoginNeedsMfa> =>
    authFetch<LoginOk | LoginNeedsMfa>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  mfaChallenge: (email: string, code: string): Promise<LoginOk> =>
    authFetch<LoginOk>("/v1/auth/mfa/challenge", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),

  mfaRecovery: (email: string, code: string): Promise<LoginOk> =>
    authFetch<LoginOk>("/v1/auth/mfa/recovery", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),

  mfaEnrol: (): Promise<MfaEnrolStart> =>
    authFetch<MfaEnrolStart>("/v1/auth/mfa/enrol", { method: "POST" }),

  mfaEnrolConfirm: (code: string): Promise<MfaEnrolConfirm> =>
    authFetch<MfaEnrolConfirm>("/v1/auth/mfa/enrol/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  logout: (): Promise<void> =>
    authFetch<void>("/v1/auth/logout", { method: "POST" }),

  passwordResetRequest: (email: string): Promise<void> =>
    authFetch<void>("/v1/auth/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  passwordResetConfirm: (
    userId: string,
    token: string,
    newPassword: string,
  ): Promise<void> =>
    authFetch<void>("/v1/auth/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({
        user_id: userId,
        token,
        new_password: newPassword,
      }),
    }),

  updateMe: (displayName: string | null): Promise<MeResponse> =>
    authFetch<MeResponse>("/v1/auth/me", {
      method: "PATCH",
      body: JSON.stringify({ display_name: displayName }),
    }),

  changePassword: (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> =>
    authFetch<void>("/v1/auth/password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),

  regenerateRecoveryCodes: (): Promise<{ recovery_codes: string[] }> =>
    authFetch<{ recovery_codes: string[] }>(
      "/v1/auth/mfa/recovery-codes/regenerate",
      { method: "POST" },
    ),

  resetMfa: (code: string): Promise<void> =>
    authFetch<void>("/v1/auth/mfa", {
      method: "DELETE",
      body: JSON.stringify({ code }),
    }),

  listSessions: (): Promise<{
    sessions: Array<{
      id: string;
      issued_at: string;
      last_seen_at: string;
      expires_at: string;
      issued_user_agent: string | null;
      issued_ip: string | null;
      is_current: boolean;
    }>;
  }> => authFetch("/v1/auth/sessions"),

  revokeSession: (id: string): Promise<void> =>
    authFetch<void>(`/v1/auth/sessions/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  revokeAllSessions: (): Promise<void> =>
    authFetch<void>("/v1/auth/sessions/revoke-all", { method: "POST" }),
};
